import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from "axios";
import {
  ASSESSMENT_API_BASE,
  AUTH_API_BASE,
  ENTERPRISE_API_BASE,
  LMS_API_BASE,
  PROFILE_API_BASE,
  SKILL_PRACTICE_API_BASE,
  SYSTEM_PARAMS_API_BASE,
  USER_API_BASE,
} from "./config";
import { storage } from "./storage";
import type { ApiResponse } from "./api-types";

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
    _authRetry?: boolean;
  }
}

const TOKEN_EXPIRY_LEEWAY_SECONDS = 30;
export const AUTH_SESSION_EXPIRED_EVENT = "fare:session-expired";
// Fired when any API call is rejected with 403 — the server's view of the
// user's permissions disagrees with the UI's, so lib/permissions re-fetches
// /auth/me.
export const PERMISSION_DENIED_EVENT = "fare:permission-denied";

type TokenResponse = {
  token?: string;
  refreshToken?: string;
};

type ApiEnvelope<T> = {
  status: string;
  message: string;
  data: T;
};

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _authRetry?: boolean;
  skipAuth?: boolean;
};

let refreshPromise: Promise<string> | null = null;

export const api = axios.create();

export function joinApiUrl(base: string, path: string) {
  const normalizedBase = base.endsWith("/") ? base : `${base}/`;
  const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
  return new URL(normalizedPath, normalizedBase).toString();
}

export function buildAuthApiUrl(path: string) {
  return joinApiUrl(AUTH_API_BASE, path);
}

export function buildUserApiUrl(path: string) {
  return joinApiUrl(USER_API_BASE, path);
}

export function buildLmsApiUrl(path: string) {
  return joinApiUrl(LMS_API_BASE, path);
}

export function buildSkillPracticeApiUrl(path: string) {
  return joinApiUrl(SKILL_PRACTICE_API_BASE, path);
}

export function buildSystemParamsApiUrl(path: string) {
  return joinApiUrl(SYSTEM_PARAMS_API_BASE, path);
}

export function buildEnterpriseApiUrl(path: string) {
  return joinApiUrl(ENTERPRISE_API_BASE, path);
}

export function buildProfileApiUrl(path: string) {
  return joinApiUrl(PROFILE_API_BASE, path);
}

export function buildAssessmentApiUrl(path: string) {
  return joinApiUrl(ASSESSMENT_API_BASE, path);
}

function decodeJwtExpiry(token: string) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const decoded = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof decoded.exp === "number" ? decoded.exp : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string, leewaySeconds = TOKEN_EXPIRY_LEEWAY_SECONDS) {
  const expiresAt = decodeJwtExpiry(token);
  if (expiresAt === null) return false;
  return expiresAt <= Math.floor(Date.now() / 1000) + leewaySeconds;
}

function expireSession() {
  storage.clear();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
  }
}

function isEnvelope<T>(payload: T | ApiEnvelope<T>): payload is ApiEnvelope<T> {
  if (!payload || typeof payload !== "object") return false;
  if (!("data" in payload)) return false;
  const record = payload as { token?: unknown; data?: unknown };
  if (typeof record.token === "string" && record.token) return false;
  return Boolean(record.data && typeof record.data === "object");
}

export function unwrapData<T>(payload: T | ApiEnvelope<T>) {
  if (isEnvelope(payload)) return payload.data;
  return payload as T;
}

function unwrapTokenResponse(payload: TokenResponse | ApiEnvelope<TokenResponse>) {
  return unwrapData(payload);
}

export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) {
    expireSession();
    return Promise.reject(new Error("No refresh token is available."));
  }

  refreshPromise = axios
    .post<TokenResponse | ApiEnvelope<TokenResponse>>(buildAuthApiUrl("/auth/refresh"), undefined, {
      params: { refreshToken },
    })
    .then(({ data }) => {
      const refreshed = unwrapTokenResponse(data);
      if (!refreshed.token) throw new Error("Refresh response did not include an access token.");
      storage.setTokens(refreshed.token, refreshed.refreshToken);
      return refreshed.token;
    })
    .catch((error) => {
      expireSession();
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

export async function ensureValidAccessToken() {
  const token = storage.getToken();
  if (!token) return "";
  return isTokenExpired(token) ? refreshAccessToken() : token;
}

function shouldSkipAuth(config?: RetryableRequestConfig) {
  if (!config) return false;
  if (config.skipAuth) return true;
  const url = String(config.url || "");
  return /\/auth\/(login|refresh)\b/.test(url);
}

api.interceptors.request.use(async (config) => {
  const request = config as RetryableRequestConfig;
  if (shouldSkipAuth(request)) return request;

  const token = await ensureValidAccessToken();
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  if (typeof FormData !== "undefined" && request.data instanceof FormData) {
    if (typeof request.headers?.delete === "function") {
      request.headers.delete("Content-Type");
      request.headers.delete("content-type");
    } else if (request.headers) {
      delete request.headers["Content-Type"];
      delete request.headers["content-type"];
    }
  }

  return request;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableRequestConfig | undefined;
    if (error.response?.status === 403 && typeof window !== "undefined" && !/\/auth\/me\b/.test(String(config?.url || ""))) {
      window.dispatchEvent(new Event(PERMISSION_DENIED_EVENT));
    }
    if (error.response?.status !== 401 || !config || config._authRetry || shouldSkipAuth(config)) {
      return Promise.reject(error);
    }

    if (!storage.getRefreshToken()) {
      expireSession();
      return Promise.reject(error);
    }

    config._authRetry = true;
    const token = await refreshAccessToken();
    config.headers.Authorization = `Bearer ${token}`;
    return api(config);
  },
);

export function axiosErrorMessage(error: unknown, fallback = "Request failed. Please try again.") {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }
  if (!error.response) {
    return "No response from server. Please check your connection.";
  }
  const body = error.response.data;
  if (typeof body === "string" && body.trim()) return body;
  if (body && typeof body === "object") {
    const record = body as { message?: unknown; error?: unknown; status?: unknown };
    if (typeof record.message === "string" && record.message.trim()) return record.message;
    if (typeof record.error === "string" && record.error.trim()) return record.error;
  }
  if (error.response.status === 401) return "Invalid username or password";
  return `Request failed with status ${error.response.status}`;
}

export async function asApiResponse<T>(request: Promise<{ data: T }>): Promise<ApiResponse<T>> {
  try {
    const { data } = await request;
    return { success: true, data: unwrapData(data) as T, message: "Success" };
  } catch (error) {
    return { success: false, error: axiosErrorMessage(error) };
  }
}

export function withSkipAuth(config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return { ...config, skipAuth: true } as AxiosRequestConfig;
}
